import { DotLegalBreadCrumbModel } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../localization/useTranslation.ts";
import { useUrlProvider } from "../useUrlProvider.ts";
import { createElement, ReactNode, useState } from "react";
import {
    ContractStandardField,
    DatePeriodConfiguration,
    DeletionResult,
    EntityTemplate,
    EntityTemplateField,
    EntityTemplateStandardField,
    FormulaToken,
    getEntityTemplateTranslationKeys,
    getFixedIncidentLogStandardFields,
    getFixedProcessStandardFields,
    getFixedLegalEntityStandardFields,
    getStandardFieldsByEntityType,
    hasIncidentLogFieldFeatureAccess,
    hasProcessFieldFeatureAccess,
    hasLegalEntityFieldFeatureAccess,
    hasSystemFieldFeatureAccess,
    isIncidentLogStandardField,
    isProcessStandardField,
    isLegalEntityStandardField,
    SelectableInputField,
    SystemStandardField,
    TemplateEntityType,
} from "./EntityTemplate.types.ts";
import { deleteHttp, post, put } from "../common/api/apiShared.ts";
import { useParams } from "react-router";
import { useOptimisticUpdate } from "../common/hooks/useOptimisticUpdate.ts";
import { useSelectableInputFields } from "../common/hooks/useSelectableItemQueries.ts";
import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import AutoRenewal from "../contract/contractTemplates/autoRenewal/AutoRenewal.tsx";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { FieldConfigurationValueType } from "./EntityTemplate.types.ts";
import { formulaFieldTypes, getNamesOfFieldsReading, serializeFormula } from "./fields/customFields/calculatedField.utils.ts";
import { parseDatePeriod, serializeDatePeriod, toDateOptionId } from "./fields/customFields/datePeriod.utils.ts";
import { ComputedFieldTemplateEditing } from "./fields/customFields/computedField.types.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useFeatureFlags, usePlanContext } from "../auth/planProvider/PlanProvider.tsx";
import { FeatureFlag, InformationSecurityFeatures, PlatformFeatures, VendorManagementFeatures } from "../auth/planProvider/PlanProvider.types.ts";

export function useEditEntityTemplate() {
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();
    const { informationSecurityFeatures, vendorManagementFeatures, platformFeatures } = usePlanContext();
    const { getEntityTemplates, getSettingsUrl } = urlProvider;
    const { addToQueueWithNoResponse } = useOptimisticUpdate();
    const [syncState, setSyncState] = useState(false);
    const snackBar = useDotLegalSnackbar();
    const featureFlags = useFeatureFlags();

    const { id } = useParams<{ id: string }>();
    const queryClient = useDotLegalQueryClient();

    const url = `/entity-templates/${id}/preview`;
    const { data: templateFromBackend, isLoading, error } = useDotLegalQuery<EntityTemplate>(url);

    const customInputFieldsQuery = useSelectableInputFields(templateFromBackend?.templateType, templateFromBackend?.templateType !== undefined);

    function getBreadCrumbLink() {
        if (templateFromBackend) {
            const templateType = templateFromBackend.templateType;
            return {
                name: translateString(getEntityTemplateTranslationKeys(templateType).templates),
                link: getEntityTemplates(templateType),
            };
        }

        return { name: "", link: "" };
    }

    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [
        { name: translateString("settings"), link: getSettingsUrl() },
        getBreadCrumbLink(),
        { name: templateFromBackend?.name ?? "" },
    ];

    // Optimistic update helper
    const updateTemplateOptimistically = (updater: (template: EntityTemplate) => EntityTemplate) => {
        queryClient.setQueryData<EntityTemplate>(url, (oldData) => {
            if (!oldData) return oldData!;
            return updater(oldData);
        });
    };

    const updateFieldOptimistically = (stepId: string, fieldId: string, patch: (field: EntityTemplateField) => EntityTemplateField) => {
        updateTemplateOptimistically((template) => ({
            ...template,
            steps: template.steps.map((step) =>
                step.id !== stepId ? step : { ...step, fields: step.fields.map((field) => (field.id === fieldId ? patch(field) : field)) }
            ),
        }));
    };

    const handleFieldReorder = async (stepId: string, movedFieldId: string, newPosition: number) => {
        updateTemplateOptimistically((template) => ({
            ...template,
            steps: template.steps.map((step) => {
                if (step.id !== stepId) return step;

                const fields = [...step.fields].sort((a, b) => a.order - b.order);
                const movedFieldIndex = fields.findIndex((f) => f.id === movedFieldId);

                if (movedFieldIndex === -1) return step;

                // Remove the moved field
                const [movedField] = fields.splice(movedFieldIndex, 1);

                // Insert at new position (newPosition is 1-based, convert to 0-based)
                const insertIndex = Math.min(newPosition - 1, fields.length);
                fields.splice(insertIndex, 0, movedField);

                // Update order numbers to match new positions
                const updatedFields = fields.map((field, index) => ({
                    ...field,
                    order: index + 1,
                }));

                // The `setSyncState` call is actually serving as a state synchronization mechanism. It ensures that - The component has immediate access to the reordered data
                setSyncState(!syncState);

                return { ...step, fields: updatedFields };
            }),
        }));
        await addToQueueWithNoResponse(reorder, {
            id: id!,
            stepId: stepId,
            movedFieldId: movedFieldId,
            newPosition: newPosition,
        });
    };

    const handleFieldDelete = async (stepId: string, fieldId: string) => {
        updateTemplateOptimistically((template) => ({
            ...template,
            steps: template.steps.map((step) => {
                if (step.id !== stepId) return step;

                const updatedFields = step.fields.filter((f) => f.id !== fieldId).map((field, index) => ({ ...field, order: index + 1 }));

                return { ...step, fields: updatedFields };
            }),
        }));

        const result = await deleteHttp<DeletionResult>(`/entity-templates/${id}/steps/${stepId}/fields/${fieldId}`);
        const deletion = result.value();
        // A failed request has no deletion result and is already reported as an error, so only a refusal names the fields
        if (deletion?.isDeleted === false) {
            snackBar.show(translateString("fieldUsedInOtherFields", { names: deletion.name }), "warning");
        }

        // Read back to undo the optimistic removal of a refused field, and because without the formula flag the backend
        // drops a deleted field from every sum that read it, a rule only the backend works out
        await queryClient.refetchQueries(url);
    };

    const handleFieldRequiredToggle = async (stepId: string, fieldId: string, required: boolean) => {
        updateFieldOptimistically(stepId, fieldId, (field) => ({
            ...field,
            required,
            requiredAtCreation: required || field.requiredAtCreationDisabled ? field.requiredAtCreation : false,
        }));

        await addToQueueWithNoResponse(changeRequired, {
            id: id!,
            stepId: stepId,
            fieldId: fieldId,
            required: required,
        });

        if (!required) {
            await addToQueueWithNoResponse(changeRequiredAtCreation, { id: id!, stepId, fieldId, requiredAtCreation: false });
        }
    };

    const handleFieldRequiredAtCreationToggle = async (stepId: string, fieldId: string, requiredAtCreation: boolean) => {
        updateFieldOptimistically(stepId, fieldId, (field) => ({ ...field, requiredAtCreation }));

        await addToQueueWithNoResponse(changeRequiredAtCreation, {
            id: id!,
            stepId: stepId,
            fieldId: fieldId,
            requiredAtCreation: requiredAtCreation,
        });
    };

    const handleFieldAutoRenewalToggle = async (stepId: string, fieldId: string, enabled: boolean) => {
        updateFieldOptimistically(stepId, fieldId, (field) => ({
            ...field,
            configurationData: { type: FieldConfigurationValueType.Boolean, value: enabled ? "true" : "false" },
        }));

        await addToQueueWithNoResponse(changeAutoRenewal, {
            id: id!,
            stepId: stepId,
            fieldId: fieldId,
            enabled: enabled,
        });
    };

    const handleFieldDefaultValueChange = async (stepId: string, fieldId: string, defaultValue: unknown) => {
        updateFieldOptimistically(stepId, fieldId, (field) => ({ ...field, defaultValue }));

        await addToQueueWithNoResponse(changeDefaultValue, { id: id!, stepId, fieldId, defaultValue });
    };

    const handleFormulaChange = async (stepId: string, fieldId: string, tokens: Array<FormulaToken>) => {
        updateFieldOptimistically(stepId, fieldId, (field) => ({ ...field, configurationData: serializeFormula(tokens) }));

        await addToQueueWithNoResponse(changeFormula, { id: id!, stepId, fieldId, tokens });
    };

    const handleDatePeriodChange = async (stepId: string, fieldId: string, configuration: DatePeriodConfiguration) => {
        updateFieldOptimistically(stepId, fieldId, (field) => ({ ...field, configurationData: serializeDatePeriod(configuration) }));

        await addToQueueWithNoResponse(changeDatePeriod, { id: id!, stepId, fieldId, configuration });
    };

    const handleFieldAdd = async (stepId: string, field: EntityTemplateField) => {
        // Optimistic update
        updateTemplateOptimistically((template) => ({
            ...template,
            steps: template.steps.map((step) => {
                if (step.id !== stepId) return step;

                return {
                    ...step,
                    fields: [...step.fields, field],
                };
            }),
        }));

        // Backend call
        const result = await post<EntityTemplateField>(`/entity-templates/${id}/steps/${stepId}/fields`, {
            standardFieldIdentifier: field.standardFieldIdentifier,
            customInputFieldId: field.customInputFieldId,
        });

        // Update with actual field data from backend (including real ID)
        if (result.value()) {
            updateTemplateOptimistically((template) => ({
                ...template,
                steps: template.steps.map((step) => {
                    if (step.id !== stepId) return step;

                    return {
                        ...step,
                        fields: step.fields.map((f) => (f.id === field.id ? result.value()! : f)),
                    };
                }),
            }));
        }
    };

    const templateWithConfiguration = templateFromBackend
        ? {
              ...templateFromBackend,
              steps: templateFromBackend.steps.map((step) => ({
                  ...step,
                  fields: step.fields.map((field) => ({
                      ...field,
                      configuration: getFieldConfiguration(step.id, field, templateFromBackend.isStandardTemplate, handleFieldAutoRenewalToggle),
                  })),
              })),
          }
        : undefined;

    return {
        isLoading: isLoading || customInputFieldsQuery.isLoading,
        breadCrumbs,
        templateFromBackend: templateWithConfiguration,
        customInputFields: filterAvailableCustomInputFields(customInputFieldsQuery.data),
        availableStandardFields: filterAvailableStandardFields(),
        error,
        handleFieldReorder,
        handleFieldDelete,
        handleFieldRequiredToggle,
        handleFieldRequiredAtCreationToggle,
        handleFieldAdd,
        handleFieldDefaultValueChange: featureFlags.isEnabled(FeatureFlag.EntityTemplateDefaultValues) ? handleFieldDefaultValueChange : undefined,
        getComputedFieldEditing,
        removeBlockedReasons: Object.fromEntries(getTemplateFields().map((field) => [field.id, getRemoveBlockedReason(field)])),
    };

    function getRemoveBlockedReason(field: EntityTemplateField): string | undefined {
        if (field.removeDisabled) {
            return translateString("removeEntityTemplateFieldDisabled");
        }

        // Without the formula flag a delete still drops the field from the sums reading it, as it did before formulas
        if (!featureFlags.isEnabled(FeatureFlag.CalculatedFieldFormulas)) {
            return undefined;
        }

        const readingFieldNames = getNamesOfFieldsReading(getTemplateFields(), field);
        return readingFieldNames.length > 0 ? translateString("fieldUsedInOtherFields", { names: readingFieldNames.join(", ") }) : undefined;
    }

    function getTemplateFields() {
        return (templateFromBackend?.steps ?? []).flatMap((step) => step.fields);
    }

    function getComputedFieldEditing(stepId: string): ComputedFieldTemplateEditing {
        return {
            formulaFields: getTemplateFormulaFields(),
            dateFields: getTemplateDateFields(),
            onFormulaChange: (fieldId, tokens) => handleFormulaChange(stepId, fieldId, tokens),
            onDatePeriodChange: (fieldId, configuration) => handleDatePeriodChange(stepId, fieldId, configuration),
        };
    }

    function getTemplateFormulaFields() {
        return getTemplateFields()
            .filter((field) => field.customInputFieldType !== undefined && formulaFieldTypes.includes(field.customInputFieldType))
            .map((field) => ({
                id: field.customInputFieldId!,
                name: field.customInputFieldName!,
                type: field.customInputFieldType!,
                periodUnit: parseDatePeriod(field.configurationData)?.unit,
            }));
    }

    // The contract dates stay selectable when they are not on the template, since the period still reads them from the contract
    function getTemplateDateFields() {
        const standardDates =
            templateFromBackend?.templateType === TemplateEntityType.Contract
                ? [
                      { id: toDateOptionId({ standardField: ContractStandardField.StartDate })!, name: translateString("startDate") },
                      { id: toDateOptionId({ standardField: ContractStandardField.EndDate })!, name: translateString("endDate") },
                  ]
                : [];
        const customDates = getTemplateFields()
            .filter((field) => field.customInputFieldType === InputFieldTypeEnum.DateField)
            .map((field) => ({ id: toDateOptionId({ customFieldId: field.customInputFieldId })!, name: field.customInputFieldName! }));

        return [...standardDates, ...customDates];
    }

    function filterAvailableStandardFields() {
        const usedStandardFields = getTemplateFields().filter((x) => x.standardFieldIdentifier);
        return getAvailableStandardFields(informationSecurityFeatures, vendorManagementFeatures, platformFeatures, templateFromBackend?.templateType).filter(
            (field) => !usedStandardFields.some((x) => x.standardFieldIdentifier === field)
        );
    }

    function filterAvailableCustomInputFields(customInputFields: Array<SelectableInputField> | undefined) {
        const usedCustomFields = getTemplateFields().filter((x) => x.customInputFieldId);

        return customInputFields?.filter((field) => !usedCustomFields.some((x) => x.customInputFieldId === field.id));
    }
}

const reorder = async (data: { id: string; stepId: string; movedFieldId: string; newPosition: number }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.movedFieldId}/reorder`, {
        newPosition: data.newPosition,
    });
};

const changeRequired = async (data: { id: string; stepId: string; fieldId: string; required: boolean }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.fieldId}/required`, {
        required: data.required,
    });
};

const changeRequiredAtCreation = async (data: { id: string; stepId: string; fieldId: string; requiredAtCreation: boolean }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.fieldId}/required-at-creation`, {
        requiredAtCreation: data.requiredAtCreation,
    });
};

const changeFormula = async (data: { id: string; stepId: string; fieldId: string; tokens: Array<FormulaToken> }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.fieldId}/formula`, { tokens: data.tokens });
};

const changeDatePeriod = async (data: { id: string; stepId: string; fieldId: string; configuration: DatePeriodConfiguration }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.fieldId}/date-period`, data.configuration);
};

const changeAutoRenewal = async (data: { id: string; stepId: string; fieldId: string; enabled: boolean }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.fieldId}/auto-renewal`, {
        enabled: data.enabled,
    });
};

const changeDefaultValue = async (data: { id: string; stepId: string; fieldId: string; defaultValue: unknown }) => {
    await put(`/entity-templates/${data.id}/steps/${data.stepId}/fields/${data.fieldId}/default-value`, { value: data.defaultValue });
};

function getFieldConfiguration(
    stepId: string,
    field: EntityTemplateField,
    isStandardTemplate: boolean,
    onAutoRenewalToggle: (stepId: string, fieldId: string, enabled: boolean) => Promise<void>
): ReactNode {
    switch (field.standardFieldIdentifier) {
        case ContractStandardField.EndDate:
            return createElement(AutoRenewal, {
                enabled: field.configurationData?.value === "true",
                onToggle: (value: boolean) => onAutoRenewalToggle(stepId, field.id, value),
                isStandard: isStandardTemplate,
            });
        default:
            return null;
    }
}

export function getAvailableStandardFields(
    informationSecurityFeatures: InformationSecurityFeatures,
    vendorManagementFeatures: VendorManagementFeatures,
    platformFeatures: PlatformFeatures,
    type?: TemplateEntityType
): Array<EntityTemplateStandardField> {
    switch (type) {
        case TemplateEntityType.Contract:
            return [
                ContractStandardField.Description,
                ContractStandardField.TotalAmount,
                ContractStandardField.FinalisedAmount,
                ContractStandardField.Currency,
                ContractStandardField.PaymentTerm,
                ContractStandardField.SpecialTerms,
                ContractStandardField.StartDate,
                ContractStandardField.EndDate,
                ContractStandardField.SignatureDate,
                ContractStandardField.NotificationOfContractExpiry,
                ContractStandardField.BusinessAreas,
                ContractStandardField.Tags,
                ContractStandardField.Note,
                ContractStandardField.ReferenceNumber,
            ];
        case TemplateEntityType.System:
            return [
                SystemStandardField.SystemDescription,
                SystemStandardField.DistributionArea,
                SystemStandardField.SystemEntityType,
                SystemStandardField.SystemStorageType,
                SystemStandardField.SystemDeletionPeriod,
                SystemStandardField.SystemNetwork,
                SystemStandardField.SystemIsCriticalForOperation,
                SystemStandardField.SystemUsesAI,
                SystemStandardField.SystemBusinessAreas,
                SystemStandardField.SystemSuppliers,
                SystemStandardField.SystemCertifications,
                SystemStandardField.SystemDataStorageLocations,
                SystemStandardField.SystemSupportAccessLocations,
                SystemStandardField.SystemSecurityMeasures,
                SystemStandardField.SystemPolicies,
                SystemStandardField.SystemDataTypes,
                SystemStandardField.SystemTags,
                SystemStandardField.SystemAssociatedAssets,
                SystemStandardField.SystemDataSubjects,
                SystemStandardField.SystemDataCategories,
            ].filter((field) => hasSystemFieldFeatureAccess(field, informationSecurityFeatures, platformFeatures));
        case TemplateEntityType.LegalEntity: {
            const fixedFields = getFixedLegalEntityStandardFields();
            return getStandardFieldsByEntityType(TemplateEntityType.LegalEntity)
                .filter(isLegalEntityStandardField)
                .filter((field) => !fixedFields.includes(field))
                .filter((field) => hasLegalEntityFieldFeatureAccess(field, informationSecurityFeatures, vendorManagementFeatures, platformFeatures));
        }
        case TemplateEntityType.IncidentLog: {
            const fixedFields = getFixedIncidentLogStandardFields();
            return getStandardFieldsByEntityType(TemplateEntityType.IncidentLog)
                .filter(isIncidentLogStandardField)
                .filter((field) => !fixedFields.includes(field))
                .filter((field) => hasIncidentLogFieldFeatureAccess(field, vendorManagementFeatures, platformFeatures));
        }
        case TemplateEntityType.Process: {
            const fixedFields = getFixedProcessStandardFields();
            return getStandardFieldsByEntityType(TemplateEntityType.Process)
                .filter(isProcessStandardField)
                .filter((field) => !fixedFields.includes(field))
                .filter((field) => hasProcessFieldFeatureAccess(field, vendorManagementFeatures, platformFeatures));
        }
        default:
            return [];
    }
}
